import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import { searchAndDownloadUnsplash, getApiBaseUrl } from '../../../services/imageService';
import fetchService from '../../../services/fetchService';
import useUsageLimitsStore from '../../../stores/usageLimitsStore';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const image_create: SimpleDocCommand = {
  id: 'image_create',
  logicalName: 'image_create',
  version: 'v2',

  description: `Create a new image file entity from AI generation (Grok), a stock photo (Unsplash), or a direct URL. The image is saved as a file entity in the workspace.

**SOURCES:** grok | unsplash | url

**EXAMPLES:**

Generate an image with Grok:
{ "source": "grok", "prompt": "modern flat vector icon of a rocket ship", "name": "rocket-icon.png" }

Download a stock photo from Unsplash:
{ "source": "unsplash", "query": "mountain landscape", "name": "hero.jpg" }

Save an image from a URL:
{ "source": "url", "url": "https://example.com/logo.png", "name": "logo.png" }

**RETURNS:** entityId and name of the created image file entity.`,

  schema: {
    parameters: [
      {
        name: 'source',
        type: 'string',
        description: 'Image source: grok | unsplash | url',
        required: true,
        enum: ['grok', 'unsplash', 'url']
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'Image generation prompt (required when source is "grok"). Be descriptive for best results.',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'grok' && (!value || value.trim().length === 0)) {
            return 'prompt is required when source is "grok"';
          }
          return null;
        }
      },
      {
        name: 'query',
        type: 'string',
        description: 'Search query for Unsplash (required when source is "unsplash").',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'unsplash' && (!value || value.trim().length === 0)) {
            return 'query is required when source is "unsplash"';
          }
          return null;
        }
      },
      {
        name: 'url',
        type: 'string',
        description: 'Direct image URL (required when source is "url"). Must start with https://.',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'url' && !value) return 'url is required when source is "url"';
          if (value && !value.startsWith('http')) return 'url must start with http:// or https://';
          return null;
        }
      },
      { name: 'name', type: 'string', description: 'Filename for the saved image (e.g. "app-icon.png"). Defaults to a generated name.', required: false },
      { name: 'folderId', type: 'string', description: 'Optional folder entity ID to place the image in.', required: false },
      { name: 'generationOptions', type: 'object', description: 'Grok generation options: { width, height, seed }. Dimensions 256–2048.', required: false },
      { name: 'unsplashOptions', type: 'object', description: 'Unsplash search options: { orientation: "landscape"|"portrait"|"squarish", color }.', required: false }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { source, prompt, query, url, name, folderId, generationOptions, unsplashOptions } = params;

    console.log('[image_create] Starting:', { source, name, folderId });

    try {
      let imageFile: File;
      let imageUrl: string;

      switch (source) {
        case 'grok': {
          const apiBaseUrl = getApiBaseUrl();
          let grokResult: any;
          try {
            grokResult = await fetchService(`${apiBaseUrl}/grok/image/generate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                prompt,
                model: generationOptions?.model || 'grok-imagine-image',
                variants: generationOptions?.variants || 1,
                seed: generationOptions?.seed,
                width: generationOptions?.width,
                height: generationOptions?.height,
              })
            });
          } catch (error: any) {
            const detail = error.data?.detail ?? error.data;
            if (detail?.upgrade_required) {
              const limitType = (detail.limit_type ?? 'imageGenerations') as any;
              useUsageLimitsStore.getState().showUpgradeModal({
                limitType,
                currentUsage: { used: detail.limits?.[limitType]?.used ?? 0, max: detail.limits?.[limitType]?.max ?? 0, unit: 'images' },
                planName: detail.plan_type ?? 'free',
                message: detail.message,
              });
              throw new Error('Image generation requires a plan upgrade.');
            }
            throw new Error(`Failed to connect to Grok API: ${error.message}`);
          }
          if (grokResult.error) throw new Error(`Grok API error: ${grokResult.error.message || grokResult.error}`);
          if (!grokResult.success || !grokResult.images?.length) throw new Error('Grok image generation returned no images');

          const base64Data: string = grokResult.images[0];
          const match = base64Data.match(/^data:image\/([a-zA-Z]*);base64,(.+)$/);
          if (!match) throw new Error('Invalid base64 image data from Grok');
          const [, imageType, actualBase64] = match;
          const byteArray = new Uint8Array(atob(actualBase64).split('').map(c => c.charCodeAt(0)));
          const blob = new Blob([byteArray], { type: `image/${imageType}` });
          const fileName = name || `grok-image.${imageType}`;
          imageFile = new File([blob], fileName, { type: `image/${imageType}` });
          imageUrl = '';
          break;
        }
        case 'unsplash': {
          const result = await searchAndDownloadUnsplash(query, unsplashOptions);
          imageUrl = result.url;
          const fetchResponse = await fetch(imageUrl);
          if (!fetchResponse.ok) throw new Error(`Failed to fetch Unsplash image: HTTP ${fetchResponse.status}`);
          const blob = await fetchResponse.blob();
          const mimeType = blob.type || 'image/jpeg';
          const fileName = name || `${query.replace(/\s+/g, '-').toLowerCase()}.jpg`;
          imageFile = new File([blob], fileName, { type: mimeType });
          break;
        }
        case 'url': {
          imageUrl = url;
          const fetchResponse = await fetch(imageUrl);
          if (!fetchResponse.ok) throw new Error(`Failed to fetch image: HTTP ${fetchResponse.status}`);
          const blob = await fetchResponse.blob();
          const mimeType = blob.type || 'image/jpeg';
          const fileName = name || url.split('/').pop()?.split('?')[0] || 'image.jpg';
          imageFile = new File([blob], fileName, { type: mimeType });
          break;
        }
        default:
          return createErrorResult('Invalid source', `source must be: grok, unsplash, or url`, 'validation');
      }

      const file = imageFile;
      console.log('[image_create] Image ready:', { name: file.name, mimeType: file.type, size: file.size });

      let spaceId: string | undefined = context.spaceId;
      if (!spaceId) {
        const { default: useSpaceStore } = await import('../../../stores/spaceStore');
        spaceId = useSpaceStore.getState().activeSpace?.id;
      }

      const { fileService } = await import('../../../services/FileService');
      const entity = await fileService.addFile(file, folderId, spaceId);

      const entityId = entity.llmId || entity._id;
      console.log('[image_create] ✅ File entity created:', { entityId, entityType: entity.entityType });

      return createSuccessResult(
        `Created image "${entity.name || file.name}" (${entityId})`,
        { entityId, entityType: entity.entityType, name: entity.name || file.name }
      );

    } catch (error: any) {
      console.error('[image_create] Error:', error);
      return createErrorResult('Failed to create image', error.message || 'Unknown error', 'execution');
    }
  }
};
